import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeOff, Sparkles, Loader2 } from "lucide-react";

export default function PremiumLoginPage() {
  const [employeeCode, setEmployeeCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(employeeCode, password);
    
    if (result.success) {
      toast.success("Welcome to V Cosmetic Crafters!");
      navigate("/");
    } else {
      toast.error(result.error || "Login failed");
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-vcc-cream-50 via-white to-vcc-pink-50 flex items-center justify-center relative overflow-hidden" data-testid="login-page">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-vcc-pink-200/30 rounded-full blur-[120px]"
          animate={{ 
            x: [0, 100, 0], 
            y: [0, 50, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-vcc-cream-200/40 rounded-full blur-[120px]"
          animate={{ 
            x: [0, -100, 0], 
            y: [0, -50, 0],
            scale: [1, 1.3, 1]
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-vcc-teal/10 rounded-full blur-[80px]"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="backdrop-blur-xl bg-white/80 border border-vcc-pink-100 rounded-3xl p-8 shadow-2xl shadow-vcc-pink-200/30">
          {/* Logo & Title */}
          <motion.div 
            className="text-center mb-8"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/vcc-logo.png" 
                alt="V Cosmetic Crafters" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <h1 className="text-2xl font-poppins font-bold text-slate-800">
              V Cosmetic Crafters
            </h1>
            <p className="text-slate-500 font-poppins text-sm">Private Limited</p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Sparkles className="w-4 h-4 text-vcc-coral" />
              <span className="text-sm text-slate-500 font-poppins">R&D Management Portal</span>
            </div>
          </motion.div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-poppins">
                Employee Code
              </label>
              <input
                type="text"
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value)}
                placeholder="Enter employee code (e.g., EMP001)"
                className="w-full px-4 py-3 bg-white border border-vcc-pink-100 rounded-xl focus:ring-2 focus:ring-vcc-teal/30 focus:border-vcc-teal outline-none transition-all font-poppins text-slate-800 placeholder-slate-400"
                data-testid="username-input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-poppins">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-white border border-vcc-pink-100 rounded-xl focus:ring-2 focus:ring-vcc-teal/30 focus:border-vcc-teal outline-none transition-all font-poppins text-slate-800 placeholder-slate-400 pr-12"
                  data-testid="password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-vcc-teal transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-vcc-teal text-white rounded-xl font-poppins font-semibold shadow-lg shadow-vcc-teal/20 hover:shadow-vcc-teal/40 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </motion.button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-400 font-poppins">
              Premium Cosmetics R&D Management System
            </p>
          </div>
        </div>

        {/* Decorative Elements */}
        <motion.div 
          className="absolute -top-8 -left-8 w-16 h-16 bg-vcc-pink-200/50 rounded-full blur-lg"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div 
          className="absolute -bottom-6 -right-6 w-20 h-20 bg-vcc-cream-200/50 rounded-full blur-lg"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </motion.div>
    </div>
  );
}
